import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearSelectedOffer,
  selectHistoryTabSelectedOffer,
  setSelectedOffer,
} from '../store/historyTabSlice.js';

// Removed useState and useMemo as they are no longer needed for search

const HistoryTab = ({ records, onRecordSelect, loading, user }) => {
  const dispatch = useDispatch();
  const selectedOffer = useSelector(selectHistoryTabSelectedOffer);
  // --- Search logic removed ---
  // The filtering is now done in CollectorPage.jsx before the 'records' prop is passed down.

  // --- HELPER: Find the latest offer in comments ---
  const getLatestOffer = (record) => {
    if (!record.comments || record.comments.length === 0) return null;

    // Find comment with status 'offer' OR has an offerAmount value
    // Sort by createdAt desc to get the latest one
    const offerComment = record.comments
      .filter((c) => c.status === 'offer' || (c.offerAmount && c.offerAmount > 0))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      
    return offerComment;
  };

  // --- HELPER: Format Date (MM/DD/YYYY) ---
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US'); // MM/DD/YYYY format
  };

  // --- HELPER: Format Currency ---
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // --- ACTION: Open Modal ---
  const handleViewOffer = (record, offerComment) => {
    dispatch(setSelectedOffer({ record, offerComment }));
  };

  // --- ACTION: Close Modal ---
  const closeOfferModal = () => {
    dispatch(clearSelectedOffer());
  };

  // --- ACTION: Copy to Clipboard ---
  const copyOfferDetails = () => {
    if (!selectedOffer) return;

    const { record, offerComment } = selectedOffer;
    
    // Logic to split names
    const nameParts = record.ptName ? record.ptName.split(' ') : ['N/A'];
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || '';

    // Logic for specific fields
    const adjNum = record.adjNumber && record.adjNumber[0] ? record.adjNumber[0].value : 'N/A';
    const offerAmt = formatCurrency(offerComment.offerAmount);
    const lien = record.lienStatus || 'N/A'; 
    const status4903 = record.status4903_8 || 'no';
    
    // Logic for C&R (check if caseStatus contains "C & R")
    const isCandR = record.caseStatus && record.caseStatus.toLowerCase().includes('c & r') ? 'yes' : 'no';

    // Construct the text exactly as requested
    const textToCopy = `Provider: ${record.provider || 'N/A'}
First Name: ${firstName}
Last Name: ${lastName}
Adj Number: ${adjNum}
Billed: ${formatCurrency(record.bill)}
Paid: ${formatCurrency(record.paid)}
Outstanding: ${formatCurrency(record.outstanding)}
OFFER: ${offerAmt}
FDS: ${formatDate(record.fds)}
LDS: ${formatDate(record.lds)}
INSURANCE: ${record.insurance || 'N/A'}
LIEN: ${lien}
4903.8: ${status4903}
AME: 
QME: 
C&R: ${isCandR}`;

    navigator.clipboard.writeText(textToCopy).then(() => {
      alert("Offer details copied to clipboard!");
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  // We no longer need 'filteredRecords', we just use 'records' directly
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading history...</p>
      </div>
    );
  }

  return (
    // Note: The main <div> wrapper from the original file is removed
    // because CollectorPage.jsx already provides padding.
    // We also remove the <h2... and search bar <div> as CollectorPage handles this.
    <>
      {records.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {/* We can't know if a search term was entered from this component anymore,
              so we simplify the message. The CollectorPage will show the correct
              message if 'searchTerm' is active and no results are found.
              
              However, the 'records' prop will be empty both for "no results" and
              "no history", so we'll just show a general message.
              The parent CollectorPage can handle the "no results for search" text.
            */}
            No records in your history match the current search.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {' '}
          {/* Added overflow-x-auto for table responsiveness */}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Provider Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Adj Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Bill
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Case Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Last Comment Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
                {/* NEW HEADER: Offer Status (Moved to Last) */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Offer Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.map(record => {
                // --- (START) LOCKING LOGIC ---
                // To re-lock overdue tasks, COMMENT OUT the line `const isLocked = false;`
                // and UNCOMMENT the line below it.
                // -----------------------------------------------------
                const isLocked = false; // CURRENT: Button is always enabled
                // const isLocked = isRecordLocked(record);
                // --- (END) LOCKING LOGIC ---
                const latestOffer = getLatestOffer(record); 
                return (
                  <tr key={record._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.provider}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.ptName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record?.adjNumber?.[0]?.value || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      ${record.bill?.toLocaleString() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.caseStatus || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Check if lastCommentDate exists before formatting */}
                      {record.lastCommentDate
                        ? new Date(record.lastCommentDate).toLocaleString()
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => onRecordSelect(record)}
                        disabled={isLocked}
                        title={
                          isLocked
                            ? 'This record is locked due to an overdue task. Contact an admin.'
                            : 'View record details'
                        }
                        className={`font-semibold ${
                          isLocked
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-indigo-600 hover:text-indigo-900'
                        }`}
                      >
                        Details
                      </button>
                    </td>
                      {/* NEW CELL: Offer Info (Moved to Last) */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {latestOffer ? (
                        <button
                          onClick={() => handleViewOffer(record, latestOffer)}
                          className="font-semibold text-indigo-600 hover:text-indigo-900"
                        >
                          View Offer (${latestOffer.offerAmount})
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* --- OFFER DETAILS POPUP MODAL --- */}
      {selectedOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Offer Details</h3>
              <button 
                onClick={closeOfferModal} 
                className="text-white hover:text-gray-200 text-xl font-bold"
              >
                &times;
              </button>
            </div>

            {/* Body - Displaying Data exact format requested */}
            <div className="p-6 space-y-2 text-sm text-gray-800 font-mono bg-gray-50 border-b">
               {/* Pre-calculating values for display */}
               {(() => {
                 const { record, offerComment } = selectedOffer;
                 const nameParts = record.ptName ? record.ptName.split(' ') : ['N/A'];
                 const firstName = nameParts[0];
                 const lastName = nameParts.slice(1).join(' ') || '';
                 const adjNum = record.adjNumber && record.adjNumber[0] ? record.adjNumber[0].value : 'N/A';
                 const isCandR = record.caseStatus && record.caseStatus.toLowerCase().includes('c & r') ? 'yes' : 'no';
                 const lien = record.lienStatus || 'N/A';

                 return (
                   <>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">Provider:</span> <span>{record.provider}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">First Name:</span> <span>{firstName}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">Last Name:</span> <span>{lastName}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">Adj Number:</span> <span>{adjNum}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">Billed:</span> <span>{formatCurrency(record.bill)}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">Paid:</span> <span>{formatCurrency(record.paid)}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">Outstanding:</span> <span>{formatCurrency(record.outstanding)}</span></div>
                    <div className="flex bg-yellow-100 p-1 -mx-1"><span className="w-32 font-bold text-indigo-700">OFFER:</span> <span className="font-bold text-indigo-700">{formatCurrency(offerComment.offerAmount)}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">FDS:</span> <span>{formatDate(record.fds)}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">LDS:</span> <span>{formatDate(record.lds)}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">INSURANCE:</span> <span>{record.insurance}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">LIEN:</span> <span>{lien}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">4903.8:</span> <span>{record.status4903_8 || 'no'}</span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">AME:</span> <span></span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">QME:</span> <span></span></div>
                    <div className="flex"><span className="w-32 font-bold text-gray-500">C&R:</span> <span>{isCandR}</span></div>
                   </>
                 );
               })()}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-white flex justify-end space-x-3">
              <button
                onClick={closeOfferModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={copyOfferDetails}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoryTab;
